import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import i18n, { useTransl } from 'shared/lib/i18n';

import GenericFormContainer from '../Forms/GenericFormContainer';
import SelectPersonContainer from './SelectPersonContainer';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';
import InfoComponent from '../Tooltip/InfoComponent';

import {
  avatarPlaceholderSRC,
  isAdmin,
  isMasterPM,
  isSuperOrConcierge,
  loadUser,
} from '../../utils';
import {
  lengthValidation120,
  maxFileSize,
  required,
  validateEmail,
  validatePhoneNumber,
} from '../Forms/utils';
import { isMasterPm } from '../User/utils';
import { checkHasRole, getFullName } from './utils';

import { MASTER_PM_FIELD } from '../Tooltip/constants';
import { roleNames } from './constants';

import {
  CHECKBOX,
  GEO,
  IMAGE_UPLOAD,
  PHONE_NUMBER,
  STYLED_TEXT,
} from '../Forms/fieldTypes';
import Info from '../Info';

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('lg')]: {
    flexWrap: 'wrap',
  },
}));

const StyledPersonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activitySection',
})(({ theme, activitySection }) => ({
  width: '100%',
  ...(activitySection && {
    width: '50%',
    paddingRight: '2rem',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  }),
}));

const StyledActivityContainer = styled(Box)(({ theme }) => ({
  width: '50%',
  [theme.breakpoints.down('lg')]: {
    width: '100%',
  },
}));

const validate = (values) => {
  const { email, phoneNumber, role, building } = values;
  const errors = {};
  if (!email && !phoneNumber) {
    errors.email = i18n.t('errors.emailOrPhoneRequired');
    errors.phoneNumber = i18n.t('errors.emailOrPhoneRequired');
  }
  const isConciergeSelected = checkHasRole(roleNames.concierge, role);
  const isBuildingRequired = isConciergeSelected;
  if (isBuildingRequired && !building?.length) {
    errors.building = i18n.t('errors.buildingsRequired');
  }
  return errors;
};

const form = 'Person';

const PersonFormComponent = (props) => {
  const { t } = useTransl();

  const {
    isCreatePM,
    ownFormData,
    person,
    onSelectExisting,
    selectExisting,
    personId,
    title,
    additionalFormData,
    disabled,
    isDisableSelectingExistingPerson,
    isAlwaysEditable,
    customActions,
    activitySection,
    customField,
    buildingId,
    onSubmit,
    formValues,
    initialValues,
    formInitialValues,
    onLoad,
    match,
  } = props;

  const isService = formValues?.isService;
  const selectedRole = formValues?.role;

  const isPMSelected =
    checkHasRole(roleNames.pmc_owner, selectedRole) ||
    checkHasRole(roleNames.property_manager, selectedRole);
  const isSISelected = checkHasRole(roleNames.superintendent, selectedRole);
  const isMasterPMChecked = formValues?.primary;

  let isMasterPMShown = isAdmin() || isMasterPM(match?.params);
  if (buildingId || personId || isService || (selectedRole && !isPMSelected)) {
    isMasterPMShown = false;
  }

  const isGeoHidden =
    get(person, 'staffRoles', []).filter(
      ({ role }) =>
        checkHasRole(roleNames.handyman, role) ||
        checkHasRole(roleNames.superintendent, role),
    ).length === 0;

  const canEditMasterPM = isAdmin() || isEqual(loadUser(), person);

  const userAvatar = formInitialValues?.hasPhoto;
  const isUploadDisabled =
    !isEmpty(formValues?.images) && formValues?.images?.length >= 1;
  const isDeletePhoto = !!formValues?.photo_deleted;

  let isEditable = true;
  let warningInfo = t('person.cantUpdatePerson');
  if (!isAlwaysEditable && person && Object.keys(person).length) {
    isEditable = person?.editable ?? true;
  }
  if (!canEditMasterPM && isMasterPm(person)) {
    isEditable = false;
    warningInfo = t('roles.cannotUpdateMasterPm');
  }
  if (person && person.hasActiveRoles === false) {
    isEditable = false;
    warningInfo = t('person.noActiveRoles');
  }

  const isDisabled =
    !isEditable || disabled || !!get(selectExisting, 'people', []).length;

  const masterPMLabel = (
    <Box display="flex" alignItems="center">
      <Box display="flex" alignItems="center">
        <div>{t('roles.masterPm')}</div>
        {isMasterPMChecked ? (
          <InfoComponent type={MASTER_PM_FIELD} isOnHover={false} />
        ) : null}
      </Box>
    </Box>
  );

  const formData = [
    {
      keyName: 'firstName',
      label: t('title.firstName'),
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
    },
    {
      keyName: 'lastName',
      label: t('title.lastName'),
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
    },
    {
      keyName: 'email',
      label: t('title.email'),
      fieldType: STYLED_TEXT,
      validate: [validateEmail],
    },
    {
      keyName: 'phoneNumber',
      label: t('title.phoneNumber'),
      fieldType: PHONE_NUMBER,
      validate: [validatePhoneNumber],
    },
    {
      keyName: 'providedGeolocation',
      fieldType: GEO,
      validate: null,
      isHidden: isGeoHidden,
    },
    {
      keyName: 'primary',
      label: masterPMLabel,
      fieldType: CHECKBOX,
      validate: null,
      isHidden: !isMasterPMShown,
    },
    {
      keyName: 'allowManageResidents',
      label: t('person.allowManageResidents'),
      fieldType: CHECKBOX,
      validate: null,
      isHidden: !isSISelected,
    },
  ];

  const photoFields = [
    {
      label: t('title.images'),
      keyName: 'images',
      fieldType: IMAGE_UPLOAD,
      validate: [maxFileSize],
      disabled: isUploadDisabled || isDeletePhoto || isDisabled,
      isHidden: isDeletePhoto,
    },
    {
      label: t('actions.deletePhoto'),
      keyName: 'photo_deleted',
      fieldType: CHECKBOX,
      validate: [],
      isHidden: !personId || !userAvatar,
      disabled: isDisabled,
    },
  ];

  const personFormData =
    ownFormData && ownFormData.length
      ? ownFormData
      : formData.concat(additionalFormData);

  const completeFormData = personFormData.concat(photoFields);

  return (
    <div>
      {!isEditable ? <Info isWarning>{warningInfo}</Info> : null}
      <StyledContainer>
        <StyledPersonContainer activitySection={activitySection}>
          {personId ? (
            <Box display="flex" justifyContent="space-between" mb="1rem">
              <ExpandableImageComponent
                src={
                  Object.keys(person).length === 0 ? null : avatarPlaceholderSRC
                }
                photo={person?.photo}
                caption={getFullName(person)}
                height="15rem"
                isSquare
              />
              {customActions}
            </Box>
          ) : null}

          <GenericFormContainer
            form={form}
            formData={completeFormData}
            initialValues={initialValues}
            onSubmit={onSubmit}
            onLoad={onLoad}
            title={title}
            isUpdate={!!personId}
            canBeUpdated={
              isAlwaysEditable || !isSuperOrConcierge(match?.params)
            }
            disabled={isDisabled}
            isCreatePM={isCreatePM}
            isFullWidthForm={Boolean(activitySection)}
            validate={validate}
            destroyOnUnmount={false}
            enableReinitialize
          />
          {customField || null}
          {!isDisableSelectingExistingPerson ? (
            <Box mt="2rem">
              <SelectPersonContainer
                onSelect={onSelectExisting}
                formValues={formValues}
              />
            </Box>
          ) : null}
        </StyledPersonContainer>
        {activitySection ? (
          <StyledActivityContainer>{activitySection}</StyledActivityContainer>
        ) : null}
      </StyledContainer>
    </div>
  );
};

PersonFormComponent.defaultProps = {
  person: {},
  personId: null,
  selectExisting: {},
  ownFormData: [],
  additionalFormData: [],
  isAlwaysEditable: false,
  isDisableSelectingExistingPerson: false,
  isCreatePM: false,
  disabled: false,
  customActions: null,
  activitySection: null,
  onSelectExisting: () => {},
  initialValues: {},
  formInitialValues: {},
  customField: null,
  buildingId: null,
  match: null,
};

PersonFormComponent.propTypes = {
  match: PropTypes.instanceOf(Object),
  selectExisting: PropTypes.instanceOf(Object),
  person: PropTypes.instanceOf(Object),
  ownFormData: PropTypes.instanceOf(Array),
  additionalFormData: PropTypes.instanceOf(Array),
  onLoad: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  personId: PropTypes.number,
  onSelectExisting: PropTypes.func,
  isCreatePM: PropTypes.bool,
  disabled: PropTypes.bool,
  isDisableSelectingExistingPerson: PropTypes.bool,
  isAlwaysEditable: PropTypes.bool,
  customActions: PropTypes.node,
  activitySection: PropTypes.node,
  onSubmit: PropTypes.func.isRequired,
  formValues: PropTypes.instanceOf(Object).isRequired,
  customField: PropTypes.node,
  buildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialValues: PropTypes.instanceOf(Object),
  formInitialValues: PropTypes.instanceOf(Object),
};

export default PersonFormComponent;
